import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { slideDown, slideUp } from 'es6-slide-up-down';
import classNames from 'classnames';
import { device } from '../../utils/styled-component-mixins';
import TertiaryButtonWYSIWYG from './TertiaryButtonWYSIWYG';
import { withWidgetSpacingWrapper } from './WidgetSpacingWrapper';
import WidgetIdWrapper from './WidgetIdWrapper';

const AccordionWrapperStyled = styled.div`
  .accordion-content {
    background-color: var(--white);
    display: none;

    & > div {
      ${(props) => (props.contentPadding ? `padding: ${props.contentPadding}` : 'padding: 4rem')}
    }
  }

  button:not(.btn) {
    border-style: none;
    border-top: 2px solid var(--grey);
    padding: 2.8rem 1.5rem;
    width: 100%;
    transition: all 0.3s ease-in-out;

    &[aria-expanded='false'] {
      background-color: var(--white);

      &:hover {
        background-color: var(--light-grey-2);
      }
    }

    &[aria-expanded='true'] {
      &.filled-bg {
        border: 0;
        background-color: var(--slate);
        color: var(--white);

        .material-icons {
          color: var(--white);
        }
      }
    }

    > div {
      align-items: center;
      display: flex;
      justify-content: space-between;

      > span:first-child {
        text-align: left;
        white-space: normal;
      }

      .material-icons {
        color: var(--charcoal-2);
        font-size: 3rem;
      }
    }
  }

  h3 {
    font-weight: 800;
    line-height: 2.4rem;

    @media ${device.mobileOnly} {
      font-size: 2rem;
    }

    @media ${device.desktop} {
      font-size: 2.2rem;
      line-height: 3.2rem;
    }
  }

  .accordion-wysiwyg {
    line-height: 2.4rem;
  }

  .title-area {
    column-gap: 1.2rem;
    display: flex;
    flex: 1;
    justify-content: space-around;
    text-wrap: wrap;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  .icon {
    max-width: 4rem;
    max-height: 3.2rem;
  }

  .accordion-title {
    flex: 1;
    display: flex;
    line-height: 3.2rem;
    font-size: 2rem;
    overflow-wrap: anywhere;
    text-align: left;
    word-wrap: break-word;
    white-space: normal;

    @media ${device.desktop} {
      font-size: 2.2rem;
    }
  }

  .invert-icon-color {
    filter: brightness(0) invert(1);
  }
`;

const Accordion = (props) => {
  const {
    pageTitle,
    icon,
    iconAltText,
    index,
    content,
    secondaryContentArea,
    deepLinkID,
    contentPadding,
    children,
    pageSubTitle,
    pageSubTitleClass,
    defaultOpen,
    filledBg = true
  } = props;
  const accordionContentRef = useRef(null);
  const accordionButtonRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        isExpanded
      });
    }
    return child;
  });

  const toggleAccordionItem = (item, toggleIfOpen = true) =>
    new Promise((resolve) => {
      if (item != null) {
        const iconElement = item.querySelector('.material-icons');

        if (item.getAttribute('aria-expanded') === 'false') {
          item.setAttribute('aria-expanded', 'true');
          slideDown(accordionContentRef.current);
          iconElement.textContent = 'remove';
          setIsExpanded(true);
        } else if (toggleIfOpen) {
          item.setAttribute('aria-expanded', 'false');
          slideUp(accordionContentRef.current);
          iconElement.textContent = 'add';
          setIsExpanded(false);
        }

        resolve();
      }
    });
  const initActiveAccordionItem = async (e) => {
    const url = window.location.hash;
    const uri = url?.split('#')[1];
    const elementToScrollTo = document.getElementById(`${uri}`);
    const currentHeaderHeight = document.querySelector('header').offsetHeight;
    const localNavHeight = document.querySelector('.local-nav')?.offsetHeight;
    const accordionItem = elementToScrollTo?.querySelectorAll('button')[0];

    if (deepLinkID && deepLinkID === uri) {
      window.history.scrollRestoration = 'manual';
      await toggleAccordionItem(accordionItem, false).then(() => {
        if (e.type === 'load') {
          window.scrollTo(0, elementToScrollTo.offsetTop - currentHeaderHeight - localNavHeight);
        }
      });
    }
  };
  useEffect(() => {
    window.addEventListener('load', initActiveAccordionItem);
    window.addEventListener('hashchange', initActiveAccordionItem);
    if (defaultOpen) {
      toggleAccordionItem(accordionButtonRef.current, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <WidgetIdWrapper widgetID={props.widgetID}>
      <AccordionWrapperStyled id={deepLinkID} contentPadding={contentPadding}>
        <h3>
          <button
            aria-expanded="false"
            aria-controls={`accordion-content-${index}`}
            id={`accordion-item-${index}-id`}
            ref={accordionButtonRef}
            onClick={(e) => toggleAccordionItem(e.currentTarget)}
            className={classNames('accordion-button', { 'filled-bg': filledBg })}
          >
            <div>
              <div className="title-area">
                {icon && (
                  <img
                    className={classNames('icon', { 'invert-icon-color': isExpanded })}
                    src={icon}
                    alt={iconAltText || ''}
                  />
                )}
                {pageSubTitle ? (
                  <span className="accordion-title">
                    {pageTitle}: <span className={pageSubTitleClass}>{pageSubTitle}</span>
                  </span>
                ) : (
                  <span className="accordion-title">{pageTitle}</span>
                )}
              </div>
              <span className="material-icons">add</span>
            </div>
          </button>
        </h3>
        <div
          className="accordion-content"
          ref={accordionContentRef}
          id={`accordion-content-${index}`}
          role="region"
          aria-labelledby={`accordion-item-${index}-id`}
        >
          {(content || secondaryContentArea) && (
            <div>
              {content && (
                <div
                  className="accordion-wysiwyg"
                  suppressHydrationWarning
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}
              {secondaryContentArea && <TertiaryButtonWYSIWYG content={secondaryContentArea} />}
            </div>
          )}
          {childrenWithProps}
        </div>
      </AccordionWrapperStyled>
    </WidgetIdWrapper>
  );
};

Accordion.defaultProps = {
  className: 'accordion-item'
};

export default withWidgetSpacingWrapper(Accordion);
